/**
 * Layer 3 of demo security stack — disable browser shortcuts that aid
 * unauthorized capture or reverse-engineering. Active ONLY when
 * user.is_demo === true (no impact on real admin operations).
 *
 * Blocked:
 *   - Right-click context menu
 *   - Text selection (CSS user-select + selectstart event)
 *   - Drag (image drag, file drag)
 *   - Common shortcuts: Ctrl/Cmd + S (save page), P (print), C (copy),
 *     U (view source), A (select all), Shift+I/J/C (devtools),
 *     F12 (devtools)
 *   - PrintScreen — best-effort: clear clipboard on keyup since browser
 *     security forbids true PrintScreen capture interception.
 *
 * NOTE: This raises friction; it does NOT make the platform impossible to
 * record (a determined attacker can use OS-level tools or a phone camera).
 * The watermark + acceptance click-through provide forensic deterrence.
 */
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

const BLOCKED_KEY_COMBOS = (e) => {
  const k = (e.key || "").toLowerCase();
  const ctrl = e.ctrlKey || e.metaKey;
  if (e.key === "F12") return true;
  if (ctrl && e.shiftKey && (k === "i" || k === "j" || k === "c")) return true;
  if (ctrl && (k === "s" || k === "p" || k === "u" || k === "c" || k === "a")) return true;
  return false;
};

export default function useDemoLockdown() {
  const { user } = useAuth();

  useEffect(() => {
    if (user?.is_demo !== true) return undefined;

    const onCtx = (e) => { e.preventDefault(); return false; };
    const onKey = (e) => {
      if (BLOCKED_KEY_COMBOS(e)) {
        e.preventDefault(); e.stopPropagation();
        return false;
      }
      // Best-effort PrintScreen handler — fires on keyup; we wipe clipboard.
      if (e.type === "keyup" && e.key === "PrintScreen") {
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText("Screenshot blocked — Delta GreenX confidential demonstration. Recording is not permitted.");
          }
        } catch { /* clipboard API may be unavailable */ }
      }
    };
    const onSelectStart = (e) => {
      // Allow selection inside form inputs/textarea so admin can still edit.
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      return false;
    };
    const onDragStart = (e) => { e.preventDefault(); return false; };
    const onCopy = (e) => { e.preventDefault(); e.clipboardData?.setData("text/plain", "Delta GreenX confidential demonstration — copy disabled."); return false; };

    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("keydown", onKey);
    document.addEventListener("keyup", onKey);
    document.addEventListener("selectstart", onSelectStart);
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("copy", onCopy);

    // CSS-level user-select: none on the body
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("keyup", onKey);
      document.removeEventListener("selectstart", onSelectStart);
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("copy", onCopy);
      document.body.style.userSelect = prev;
    };
  }, [user]);
}
